import React, { useMemo } from 'react';
import {
  Table,
  ScrollArea,
  Loader,
  TextInput,
  Group,
  Center,
  Text,
  Pagination,
  ActionIcon,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useReactTable, getCoreRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table';
import { IconSearch, IconMaximize } from '@tabler/icons-react';

import { useVirtualizer } from '@tanstack/react-virtual';

const B2BTableGrid = ({
  columns,
  data,
  orderSearch,
  setOrderSearch,
  isLoading = false,
  isError = false,
  enableTopToolbar = true,
  pagination,
  rowCount,
  onPaginationChange,
  searchTerm = '',
  globalFilter,
  enableFullScreenToggle = false,
  handleSearchChange = () => { },
  enableRowSelection = false,
  onRowClick = () => { },
}) => {

  const theme = useMantineTheme();

  const table = useReactTable({
    data: data || [],
    columns: columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    enableRowSelection,
  });


  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => document.querySelector('.mantine-ScrollArea-viewport'),
    estimateSize: () => 48,
    overscan: 10,
  });

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>

      {isError ? (
        <Center py="lg">
          <Text c="red" size="md" weight={500}>
            Something went wrong while loading data.
          </Text>
        </Center>
      ) : (
        <ScrollArea
          type="scroll"
          scrollbarSize={4}
          offsetScrollbars
          style={{
            maxHeight: '70vh',
            width: '100%',
            '--scrollarea-scrollbar-size': '4px',
          }}
          styles={{
            root: { touchAction: 'pan-x pan-y' },
            scrollbar: {
              display: 'none',
            },
            thumb: {
              display: 'none',
            },
          }}
        >
          <div style={{ minWidth: 1000, width: '100%' }}>
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              verticalSpacing="sm"
              horizontalSpacing="md"
              style={{
                tableLayout: 'auto',
                fontSize: theme.fontSizes.sm,
                '--table-row-height': '48px',
              }}
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                  zIndex: 1,
                  boxShadow: theme.shadows.sm,
                }}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: theme.spacing.sm,
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          minWidth: header.column.columnDef.minWidth || 100,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length}>
                      <Center py="lg">
                        <Text size="md" c="dimmed">
                          No data found.
                        </Text>
                      </Center>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => onRowClick(row.original)}
                      style={{
                        cursor: enableRowSelection ? 'pointer' : 'default',
                        background: row.getIsSelected()
                          ? theme.colors.blue[1]
                          : 'inherit',
                      }}
                      role="row"
                      aria-selected={row.getIsSelected()}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            textAlign: 'center',
                            verticalAlign: 'middle',
                            padding: theme.spacing.sm,
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </ScrollArea>
      )}

      <Group mt="md" mb="md" justify="flex-end" px="sm">
        <Text size="sm" c="dimmed">
          Showing {pagination.pageIndex * pagination.pageSize + 1} to{' '}
          {Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount)} of {rowCount}{' '}
          entries
        </Text>
        <Pagination
          total={Math.ceil(rowCount / pagination.pageSize)}
          value={pagination.pageIndex + 1}
          color='#8BC53F'
          onChange={(page) => onPaginationChange({ ...pagination, pageIndex: page - 1 })}
          size="sm"
        />
      </Group>
    </div>
  );
};

export default B2BTableGrid;